import { ElMessage } from "element-plus";
import type { ProjectStatus } from "../types";

const statusMap = {
  ready: { label: "就绪", color: "primary" },
  deploying: { label: "部署中", color: "warning" },
  deployFailure: { label: "部署失败", color: "danger" },
  deploySuccess: { label: "部署成功", color: "success" },
  destroying: { label: "销毁中", color: "warning" },
  destroyFailure: { label: "销毁失败", color: "danger" },
  destroySuccess: { label: "销毁成功", color: "success" },
};

export const getStatus = (status: ProjectStatus) => {
  return statusMap[status] || { label: "未知", color: "info" };
};

// 设置url参数
export const setUrlParam = (key: string, value: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.pushState({}, "", url.toString());
};

// 获取url参数
export const getUrlParam = (key: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(key);
};

// 删除url参数
export const deleteUrlParam = (key: string) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);
  window.history.pushState({}, "", url.toString());
};

/**
 * 生产环境可用的剪贴板复制函数（带完整容错和降级）
 * @param {string} text 要复制的文本
 * @returns {Promise<boolean>} 复制结果
 */
export async function safeCopyToClipboard(text: string) {
  // 1. 基础校验
  if (typeof text !== "string" || !text.trim()) {
    console.error("复制失败：文本内容为空");
    return false;
  }

  try {
    // 2. 优先使用 Clipboard API（现代浏览器）
    // 先检查 Clipboard API 是否可用
    if (!navigator.clipboard) {
      throw new Error("Clipboard API 不可用");
    }
    await navigator.clipboard.writeText(text);
    ElMessage.success("复制成功");
    return true;
  } catch (clipboardError: any) {
    console.warn("Clipboard API 失败：", clipboardError.message);
    // 3. 降级方案：使用 execCommand（兼容 HTTP/老浏览器）
    return fallbackCopy(text);
  }
}

/**
 * 降级复制方案（不依赖 HTTPS，兼容所有浏览器）
 * @param {string} text 要复制的文本
 * @returns {boolean} 复制结果
 */
function fallbackCopy(text: string) {
  // 创建临时文本框
  const textArea = document.createElement("textarea");
  textArea.value = text;
  // 隐藏文本框（避免页面闪烁）
  textArea.style.position = "fixed";
  textArea.style.top = "-9999px";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);

  // 选中文本
  textArea.focus();
  textArea.select();

  try {
    // 执行复制
    const success = document.execCommand("copy");
    if (!success) {
      throw new Error("execCommand 执行失败");
    }
    ElMessage.success("复制成功");
    return true;
  } catch (fallbackError) {
    console.error("所有复制方案均失败：", fallbackError);
    ElMessage.error("复制失败，请手动复制");
    return false;
  } finally {
    // 移除临时元素
    document.body.removeChild(textArea);
  }
}
